import {
  doc,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../config';
import type { OcrCreditUsageType } from '../../../../types/ocrCreditUsage';
import { creditGrantTypes } from '../../../../constants/enum';

const companiesRef = collection(db, 'companies');

/**
 * 最短有効期限のクレジット情報を取得
 * 残額が残っている付与レコードのうち、有効期限が最も近いものを取得
 * 同じ有効期限の付与が複数ある場合は金額を合算
 * @param companyId - 対象企業ID
 * @returns 最短有効期限のクレジット情報（有効期限と合計金額）、なければnull
 */
export const getEarliestExpiringCredit = async (
  companyId: string,
): Promise<{ expiresAt: Date; totalAmount: number } | null> => {
  const companyDocRef = doc(companiesRef, companyId);
  const usageCollectionRef = collection(companyDocRef, 'ocr_credit_usage');

  // 付与レコードのみ取得（remaining_amount > 0 かつ expires_at が存在）
  // creditGrantTypes: FIFO消費対象の付与種別（constants/enum.tsで一元管理）
  const q = query(
    usageCollectionRef,
    where('usage_type', 'in', creditGrantTypes),
    orderBy('expires_at', 'asc'),
  );

  const snapshot = await getDocs(q);
  if (snapshot.empty) {
    return null;
  }

  // 残額がある かつ 有効期限内の付与レコードのみフィルタリング
  // 有効期限はその日（JST）の終わりまで有効とする
  const now = new Date();
  const jstOffset = 9 * 60 * 60 * 1000;
  const jstTime = new Date(now.getTime() + jstOffset);
  const startOfTodayJST = new Date(
    Date.UTC(jstTime.getUTCFullYear(), jstTime.getUTCMonth(), jstTime.getUTCDate()) - jstOffset,
  );
  const validCredits: OcrCreditUsageType[] = [];
  snapshot.forEach(docSnap => {
    const data = docSnap.data();
    if (data.remaining_amount && data.remaining_amount > 0 && data.expires_at) {
      const expiresAt =
        data.expires_at instanceof Timestamp ? data.expires_at.toDate() : data.expires_at;
      // expires_atが今日（JST）より前なら期限切れとして除外
      if (expiresAt >= startOfTodayJST) {
        validCredits.push({
          id: docSnap.id,
          ...data,
          created_at:
            data.created_at instanceof Timestamp ? data.created_at.toDate() : data.created_at,
          expires_at: expiresAt,
        } as OcrCreditUsageType);
      }
    }
  });

  if (validCredits.length === 0) {
    return null;
  }

  // 最短有効期限を取得（フィルタリング済みなので存在が保証されている）
  const firstCredit = validCredits[0];
  const earliestExpiry = firstCredit.expires_at as Date;

  // 同じ有効期限の付与を合算
  const sameExpiryCredits = validCredits.filter(credit => {
    const creditExpiry = credit.expires_at as Date;
    return (
      creditExpiry.getFullYear() === earliestExpiry.getFullYear() &&
      creditExpiry.getMonth() === earliestExpiry.getMonth() &&
      creditExpiry.getDate() === earliestExpiry.getDate()
    );
  });

  const totalAmount = sameExpiryCredits.reduce(
    (sum, credit) => sum + (credit.remaining_amount || 0),
    0,
  );

  return {
    expiresAt: earliestExpiry,
    totalAmount,
  };
};

/**
 * 企業にクレジット付与履歴が存在するかを判定
 * 有効期限が過ぎていても、過去に付与されたことがあれば true を返す。
 * 履歴モーダルへの導線を維持するための判定に使用する。
 * @param companyId - 対象企業ID
 * @returns 付与履歴が1件以上あれば true
 */
export const hasOcrCreditGrants = async (companyId: string): Promise<boolean> => {
  const companyDocRef = doc(companiesRef, companyId);
  const usageCollectionRef = collection(companyDocRef, 'ocr_credit_usage');
  const q = query(usageCollectionRef, where('usage_type', 'in', creditGrantTypes), limit(1));
  const snapshot = await getDocs(q);
  return !snapshot.empty;
};

/**
 * OCRクレジット使用履歴一覧を取得
 * @param companyId - 対象企業ID
 * @returns 使用履歴一覧（作成日時降順）
 */
export const getOcrCreditUsageHistory = async (
  companyId: string,
): Promise<OcrCreditUsageType[]> => {
  const companyDocRef = doc(companiesRef, companyId);
  const usageCollectionRef = collection(companyDocRef, 'ocr_credit_usage');

  const q = query(usageCollectionRef, orderBy('created_at', 'desc'));

  const snapshot = await getDocs(q);
  const usageHistory: OcrCreditUsageType[] = [];

  snapshot.forEach(docSnap => {
    const data = docSnap.data();
    usageHistory.push({
      id: docSnap.id,
      ...data,
      created_at: data.created_at instanceof Timestamp ? data.created_at.toDate() : data.created_at,
      expires_at: data.expires_at instanceof Timestamp ? data.expires_at.toDate() : data.expires_at,
    } as OcrCreditUsageType);
  });

  return usageHistory;
};
